import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Stack, Table, TableBody, TableContainer, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AuthService } from '../../services/auth.service';
import { QcInspectionService } from '../../services/qcInspection.service';
import { useToast } from '../../context/ToastContext';
import { IQcInspection } from '../../interfaces/garments/qcInspection.interface';
import { QC_TABLE_HEAD, QcRow, useQcActions } from './QcInspectionList';

/** Inspections of one buyer order, shown on the order page. Self-fetching, like the T&A panel. */
export default function OrderQcPanel({ buyerOrderId, canManage }: { buyerOrderId: string; canManage: boolean }) {
    const toast = useToast();
    const [rows, setRows] = useState<IQcInspection[]>([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        const inst = AuthService.getSelectedInstitution();
        if (!inst) return;
        setLoading(true);
        try {
            setRows((await QcInspectionService.list(inst, { buyerOrderId }, 0, 50)).content);
        } catch (e) {
            toast.showApiError(e, 'Failed to load inspections');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyerOrderId]);

    useEffect(() => { load(); }, [load]);
    const { actions, openBooking, dialogs } = useQcActions(load, toast);

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">Quality inspections</Typography>
                {canManage && (
                    <Button size="small" startIcon={<AddIcon />} onClick={openBooking} data-help="merchandising.qc.book">Book inspection</Button>
                )}
            </Stack>
            {loading ? <CircularProgress size={20} /> : rows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No inspections booked for this order.</Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        {QC_TABLE_HEAD}
                        <TableBody>{rows.map(q => <QcRow key={q.id} q={q} canManage={canManage} actions={actions} />)}</TableBody>
                    </Table>
                </TableContainer>
            )}
            {dialogs(buyerOrderId)}
        </Box>
    );
}
